import React, { FC } from 'react';

import {
  Tab,
  Tabs,
  Flex,
  Button,
  Heading,
  TabList,
  TabPanel,
  TabPanels,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react';

import { routesSettings } from 'constants/routes';
import { useNavigate } from 'react-router-dom';

import {
  MdAddLocationAlt,
  MdFileUpload,
  MdClose,
  MdSearch,
} from 'react-icons/md';

import { UserPill } from 'components/core/UserPill';
import { washHub } from 'store';
import AllSites from './AllSites';
import ActiveSites from './ActiveSites';
import InactiveSites from './InactiveSites';
import ArchivedSites from './ArchivedSites';

import { CustomerSitesViewProps } from './CustomerSites.props';

const _selected = {
  color: 'support.500',
  backgroundColor: 'primary.900',
};

const CustomerSitesView: FC<CustomerSitesViewProps> = (props) => {
  const {
    onDelete,
    isDeleting,
    defaultIndex,
    setDefaultIndex,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sort,
    setSort,
    onExport,
    isExporting,
  } = props;

  const navigate = useNavigate();
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const handleSearch = () => {
    // Search is handled via the search state
  };

  const handleSearchClear = () => {
    setSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const sortHandler = (field: string) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      const sortOrder = sort === undefined || sort === 1 ? -1 : 1;
      setSort(sortOrder);
    } else {
      // Set new field and default to ascending
      setSortBy(field);
      setSort(1);
    }
  };

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading fontSize="24px">Customer Sites</Heading>
        <Flex align="flex-end" gap="10px">
          <Button
            isDisabled={washHubId === ''}
            minW="200px"
            leftIcon={<MdAddLocationAlt size="20px" />}
            onClick={() => {
              navigate(routesSettings.PORTAL_CREATE_SITE.path);
            }}>
            Create Site
          </Button>
          <Button
            minW="200px"
            leftIcon={<MdFileUpload size="20px" />}
            onClick={onExport}
            isLoading={isExporting}
            loadingText="Exporting...">
            Export Sites
          </Button>
          <UserPill />
        </Flex>
      </Flex>

      <Flex my={6} align="flex-end">
        <Stack spacing={1} minW="240px" maxW="240px" w="100%">
          <Text color="textColor">Search Site Name</Text>
          <InputGroup>
            <Input
              ref={searchInputRef}
              placeholder="Enter site name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <InputRightElement>
              <MdSearch fill="#2FB64A" />
            </InputRightElement>
          </InputGroup>
        </Stack>
        {search && (
          <IconButton
            ml={3}
            aria-label="clear-search"
            icon={<MdClose size="22px" />}
            onClick={handleSearchClear}
          />
        )}
        <Button ml={3} minW="200px" isDisabled={!search} onClick={handleSearch}>
          Search
        </Button>
      </Flex>

      <Tabs
        mt="20px"
        variant="enclosed"
        size="lg"
        index={defaultIndex || 0}
        defaultIndex={defaultIndex || 0}
        onChange={(item) => {
          setDefaultIndex(item);
        }}>
        <TabList w="100%" color="support.500">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Active
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Inactive
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Archived
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <AllSites
              onDelete={onDelete}
              isDeleting={isDeleting}
              search={search}
              sortBy={sortBy}
              sort={sort}
              sortHandler={sortHandler}
            />
          </TabPanel>
          <TabPanel p={0}>
            <ActiveSites
              onDelete={onDelete}
              isDeleting={isDeleting}
              search={search}
              sortBy={sortBy}
              sort={sort}
              sortHandler={sortHandler}
            />
          </TabPanel>
          <TabPanel p={0}>
            <InactiveSites
              onDelete={onDelete}
              isDeleting={isDeleting}
              search={search}
              sortBy={sortBy}
              sort={sort}
              sortHandler={sortHandler}
            />
          </TabPanel>
          <TabPanel p={0}>
            <ArchivedSites
              onDelete={onDelete}
              isDeleting={isDeleting}
              search={search}
              sortBy={sortBy}
              sort={sort}
              sortHandler={sortHandler}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default CustomerSitesView;
